import {
  MRT_GlobalFilterTextField as MRTGlobalFilterTextField,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_TableInstance,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Box, Button, Grid2, IconButton, Tooltip, Typography } from "@mui/material";
import { AddBox, Delete, FileDownload } from "@mui/icons-material";
import { UserManagementRow } from "./typings/user-management.type";
import ExcelUploadModal from "../../../components/ExcelUpload/excel-upload.modal";
import { bulkUserExcelUpload } from "../../../utils/axios-requests";

type Props = {
  table: MRT_TableInstance<UserManagementRow>;
  disabledDeleteButton: boolean;
  onDelete: () => void;
  onReload: () => Promise<void>;
  onExportData: () => void;
};
export const UserManagementTopToolbar = ({
  table,
  disabledDeleteButton,
  onDelete,
  onReload,
  onExportData
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  return (
    <Grid2
      container
      data-testid="user-management-top-toolbar"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "space-between",
        gap: 2,
        p: 2,
        width: "100%"
      }}
    >
      <Typography variant={"h5"} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        {t("User")}
      </Typography>

      <Grid2
        data-testid="user-management-toolbar-controls"
        sx={{
          display: "flex",
          flex: { md: "1 1 auto" },
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: { xs: "flex-start", md: "flex-end" },
          gap: 1,
          minWidth: 0,
          width: { xs: "100%", md: "auto" }
        }}
      >
        <Box
          data-testid="user-management-toolbar-search"
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 260px", md: "0 1 320px" },
            minWidth: 0,
            "& .MuiFormControl-root": { width: "100%" }
          }}
        >
          <MRTGlobalFilterTextField table={table} />
        </Box>

        <Box
          data-testid="user-management-toolbar-actions"
          sx={{
            display: "flex",
            flex: { xs: "1 1 100%", sm: "0 1 auto" },
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            minWidth: 0
          }}
        >
          <MRTToggleFiltersButton table={table} />
          <MRTShowHideColumnsButton table={table} />

          <Tooltip
            title={<Typography>{t("Delete selection")}</Typography>}
            aria-label={"Delete selection"}
          >
            <span>
              <IconButton
                color="error"
                data-testid="handle-remove-user-btn"
                disabled={disabledDeleteButton}
                onClick={onDelete}
                size="large"
              >
                <Delete />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={<Typography>{t("Export")}</Typography>} aria-label={"Export"}>
            <IconButton data-testid="Export-users-btn" onClick={onExportData} size="large">
              <FileDownload />
            </IconButton>
          </Tooltip>

          <ExcelUploadModal
            onFinishUpload={() => {
              enqueueSnackbar(t("Successfully uploaded users"), {
                variant: "success"
              });
            }}
            uploadCall={bulkUserExcelUpload}
            triggerCall={onReload}
          />

          <Button
            data-testid="Add-new-user-btn"
            onClick={() => table.setCreatingRow(true)}
            sx={{ height: "fit-content", alignSelf: "center", p: "6px 12px" }}
          >
            <AddBox sx={{ mr: 1 }} />
            {t("Add User")}
          </Button>
        </Box>
      </Grid2>
    </Grid2>
  );
};
